/** Legend parsing shared by the SafeSystem apps' `parseLegendLayers`. */

import type { ILayerSource } from '#lib/types.js';
import { ECrashes, ECrashesModule } from '#lib/apps/safesystem/context/interfaces.js';
import type { MapLayer, MapLegendItem } from '#lib/stores/legendStore.svelte.js';
import { PATCH_FALLBACK } from '#lib/vizPalette.js';

/** True when the layer's minzoom hides it at the current map zoom. */
export function disableByZoom(minzoom: number | undefined, zoom: number) {
	return minzoom !== undefined && zoom < minzoom;
}

type PaintProp = 'line-color' | 'circle-color' | 'fill-color' | 'text-color';

interface ParseResult {
	legendItems: MapLegendItem[];
	filterAttr: string;
	defaultEnabled: boolean;
}

export type Parsers = Record<string, (feature: ILayerSource) => ParseResult>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const paintOf = (feature: ILayerSource, prop: PaintProp) => (feature.layer as any).paint[prop];

export function parseMatchPaint(
	feature: ILayerSource,
	paintProp: PaintProp,
	options: {
		defaultEnabled?: boolean;
		labelFor?: (raw: string) => string;
		idSuffixFor?: (raw: string) => string;
	} = {}
): ParseResult {
	const { defaultEnabled = false, labelFor, idSuffixFor } = options;
	const paintArray = paintOf(feature, paintProp) as string[];
	const filterAttr = paintArray[1][1];
	const legendItems: MapLegendItem[] = [];
	for (let i = 2; i < paintArray.length - 1; i += 2) {
		const raw = paintArray[i];
		legendItems.push({
			legendItemId: `${feature.id}:${idSuffixFor ? idSuffixFor(raw) : raw.replace(' ', '_')}`,
			filterValue: raw,
			label: labelFor ? labelFor(raw) : raw,
			color: paintArray[i + 1],
			enabled: defaultEnabled
		});
	}
	return { legendItems, filterAttr, defaultEnabled };
}

export function parseSinglePaint(
	feature: ILayerSource,
	paintProp: PaintProp,
	idSuffix: string,
	label: string
): ParseResult {
	return {
		legendItems: [
			{
				legendItemId: `${feature.id}:${idSuffix}`,
				label,
				color: paintOf(feature, paintProp) as string,
				enabled: false
			}
		],
		filterAttr: '',
		defaultEnabled: false
	};
}

const crashParsers: Parsers = {
	[ECrashes.CrashesCircle]: (f) => parseSinglePaint(f, 'circle-color', 'circle', ''),
	[ECrashes.CrashesHeatmap]: (f) => ({
		legendItems: [
			{ legendItemId: `${f.id}:heatmap`, label: '', color: PATCH_FALLBACK, enabled: false }
		],
		filterAttr: '',
		defaultEnabled: false
	}),
	[ECrashes.UploadedCrashes]: (f) => parseMatchPaint(f, 'circle-color', { defaultEnabled: true })
};

const emptyResult: ParseResult = { legendItems: [], filterAttr: '', defaultEnabled: false };

/**
 * Converts every feature in `modules` plus the crash modules into `legendStore` layers;
 * `layerProps` decides each layer's initial visibility and map-data-filter opt-out.
 */
export function buildLegendLayers(
	payload: object,
	modules: string[],
	parsers: Parsers,
	layerProps: (
		module: string,
		defaultEnabled: boolean,
		layerId: string
	) => { enabled: boolean; excludeFromMapDataFilter: boolean }
): MapLayer[] {
	const byModule = payload as Record<string, Record<string, ILayerSource | null> | undefined>;
	const allParsers = { ...crashParsers, ...parsers };
	return [...modules, ...Object.values(ECrashesModule)].flatMap((module) =>
		Object.values(byModule[module] ?? {}).flatMap((feature): MapLayer[] => {
			if (!feature) return [];
			const { legendItems, filterAttr, defaultEnabled } = (
				allParsers[feature.id] ?? (() => emptyResult)
			)(feature);
			const { enabled, excludeFromMapDataFilter } = layerProps(module, defaultEnabled, feature.id);
			if (enabled !== defaultEnabled) for (const item of legendItems) item.enabled = enabled;
			return [
				{
					systemId: feature.id,
					layerId: feature.layer.id as string,
					initialFilter: feature.layer.filter,
					minzoom: feature.layer.minzoom,
					filterAttr,
					legendItems,
					layerAllEnabled: enabled,
					layerAnyEnabled: enabled,
					excludeFromMapDataFilter
				}
			];
		})
	);
}
